import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import { StreakProvider } from "@/context/StreakContext";
import { AccentProvider } from "@/context/AccentContext";
import { Header } from "@/components/Header";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AuthCallback } from "@/components/AuthCallback";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Chat from "@/pages/Chat";
import TransMessages from "@/pages/TransMessages";
import Notes from "@/pages/Notes";
import Pricing from "@/pages/Pricing";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";
import Phrasebook from "@/pages/Phrasebook";
import News from "@/pages/News";
import { JoinRoom, JoinTeam } from "@/pages/Join";
import { MusicGeneration, ImageGenerator, Summarizers } from "@/pages/Tools";
import { LogoMaker, TattooDesigner, AIDetector } from "@/pages/EliteTools";
import Creations from "@/pages/Creations";
import Stories from "@/pages/Stories";
import StoryDetail from "@/pages/StoryDetail";
import Bedtime from "@/pages/Bedtime";
import Progress from "@/pages/Progress";
import Buddy from "@/pages/Buddy";
import Listen from "@/pages/Listen";
import SharedStory from "@/pages/SharedStory";
import SharedPrompt from "@/pages/SharedPrompt";
import SharedPhrase from "@/pages/SharedPhrase";
import SharedPack from "@/pages/SharedPack";
import SharedLetter from "@/pages/SharedLetter";
import SharedCalendar from "@/pages/SharedCalendar";
import SharedFolder from "@/pages/SharedFolder";
import SharedWalk from "@/pages/SharedWalk";
import AllPrompts from "@/pages/AllPrompts";
import Calendar from "@/pages/Calendar";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import Support from "@/pages/Support";
import Tour from "@/pages/Tour";
import Press from "@/pages/Press";
import HallOfFame from "@/pages/HallOfFame";
import { MiluAssistant } from "@/components/MiluAssistant";
import { IncomingCall } from "@/components/IncomingCall";
import { FirstTourDialog } from "@/components/FirstTourDialog";

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <Header />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/support" element={<Support />} />
        <Route path="/tour" element={<Tour />} />
        <Route path="/press" element={<Press />} />
        <Route path="/hall-of-fame" element={<HallOfFame />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
        <Route path="/messages" element={<ProtectedRoute><TransMessages /></ProtectedRoute>} />
        <Route path="/notes" element={<ProtectedRoute><Notes /></ProtectedRoute>} />
        <Route path="/phrasebook" element={<ProtectedRoute><Phrasebook /></ProtectedRoute>} />
        <Route path="/news" element={<ProtectedRoute><News /></ProtectedRoute>} />
        <Route path="/prompts" element={<ProtectedRoute><AllPrompts /></ProtectedRoute>} />
        <Route path="/calendar" element={<ProtectedRoute><Calendar /></ProtectedRoute>} />
        <Route path="/tools/music" element={<ProtectedRoute><MusicGeneration /></ProtectedRoute>} />
        <Route path="/tools/image" element={<ProtectedRoute><ImageGenerator /></ProtectedRoute>} />
        <Route path="/tools/summarize" element={<ProtectedRoute><Summarizers /></ProtectedRoute>} />
        <Route path="/tools/logo" element={<ProtectedRoute><LogoMaker /></ProtectedRoute>} />
        <Route path="/tools/tattoo" element={<ProtectedRoute><TattooDesigner /></ProtectedRoute>} />
        <Route path="/tools/detector" element={<ProtectedRoute><AIDetector /></ProtectedRoute>} />
        <Route path="/creations" element={<ProtectedRoute><Creations /></ProtectedRoute>} />
        <Route path="/stories" element={<ProtectedRoute><Stories /></ProtectedRoute>} />
        <Route path="/stories/:storyId" element={<ProtectedRoute><StoryDetail /></ProtectedRoute>} />
        <Route path="/bedtime" element={<ProtectedRoute><Bedtime /></ProtectedRoute>} />
        <Route path="/progress" element={<ProtectedRoute><Progress /></ProtectedRoute>} />
        <Route path="/buddy" element={<ProtectedRoute><Buddy /></ProtectedRoute>} />
        <Route path="/listen/:token" element={<Listen />} />
        <Route path="/buddy/:code" element={<ProtectedRoute><Buddy /></ProtectedRoute>} />
        <Route path="/s/:shareId" element={<SharedStory />} />
        <Route path="/p/:shareId" element={<SharedPrompt />} />
        <Route path="/ph/:shareId" element={<SharedPhrase />} />
        <Route path="/pk/:shareId" element={<SharedPack />} />
        <Route path="/lt/:shareId" element={<SharedLetter />} />
        <Route path="/calendar/shared/:token" element={<SharedCalendar />} />
        <Route path="/news/shared/:token" element={<SharedFolder />} />
        <Route path="/news/walk/:token" element={<SharedWalk />} />
        <Route path="/join/:code" element={<JoinRoom />} />
        <Route path="/invite/team/:token" element={<JoinTeam />} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
      </Routes>
      <MiluAssistant />
      <IncomingCall />
      <FirstTourDialog />
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <I18nProvider>
          <StreakProvider>
            <AccentProvider>
              <AppRouter />
              <Toaster position="top-center" />
            </AccentProvider>
          </StreakProvider>
        </I18nProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
